import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import BeforeAfter from "@/components/BeforeAfter";
import { buildBreadcrumbs, buildProject } from "@/lib/jsonLd";
import { ArrowLeft, ArrowRight, ArrowUpRight, MapPin, Calendar, Layers, ShieldCheck } from "lucide-react";

const CATEGORIE_LABEL = {
  particulier: "Particulier",
  sinistre: "Sinistre",
  professionnel: "Professionnel",
};

export default function RealisationDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([api.get(`/realisations/${slug}`), api.get(`/realisations`)])
      .then(([res, list]) => {
        setR(res.data);
        setAll(list.data || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Prev/next navigation between realisations
  const { prev, next } = useMemo(() => {
    if (!r || all.length === 0) return { prev: null, next: null };
    const idx = all.findIndex((x) => x.slug === r.slug);
    return {
      prev: idx > 0 ? all[idx - 1] : all[all.length - 1],
      next: idx >= 0 && idx < all.length - 1 ? all[idx + 1] : all[0],
    };
  }, [r, all]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <p className="text-serial">Chargement</p>
      </div>
    );
  }
  if (notFound || !r) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <p className="text-eyebrow mb-3">Erreur</p>
        <h1 className="font-serif text-4xl mb-6">Dossier introuvable.</h1>
        <Link to="/realisations" className="btn-ink inline-flex items-center gap-3">
          Toutes les réalisations <ArrowUpRight size={14} />
        </Link>
      </div>
    );
  }

  const paras = (r.contenu || "").split(/\n\n+/).filter(Boolean);
  const cat = CATEGORIE_LABEL[r.categorie] || "Projet";
  const validPairs = (r.avant_apres || []).filter((p) => p.avant_url && p.apres_url);

  return (
    <article data-testid={`realisation-detail-${r.slug}`}>
      <Seo
        title={r.titre}
        description={r.chapeau || r.sous_titre || `Dossier ${cat} · ${r.titre}`}
        path={`/realisations/${r.slug}`}
        image={r.photo_couverture}
        jsonLd={[
          buildProject(r),
          buildBreadcrumbs([
            { name: "Réalisations", path: "/realisations" },
            { name: r.titre, path: `/realisations/${r.slug}` },
          ]),
        ]}
      />

      {/* ── HERO IMMERSIF : image full-bleed + overlay + titre superposé ── */}
      <section className="relative bg-foreground text-background">
        <div className="absolute top-6 md:top-8 left-5 md:left-10 z-10">
          <Link
            to="/realisations"
            className="inline-flex items-center gap-2 bg-background/10 backdrop-blur-md border border-background/20 px-3.5 py-2 text-[11px] tracking-[0.18em] uppercase font-medium text-background hover:bg-background hover:text-foreground transition-colors"
            data-testid="detail-back"
          >
            <ArrowLeft size={13} /> Retour
          </Link>
        </div>

        <div className="relative aspect-[16/10] md:aspect-[16/8] max-h-[78vh] w-full overflow-hidden">
          <img
            src={r.photo_couverture}
            alt={r.titre}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* gradient bottom for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/30 to-transparent" />
          {/* title overlay */}
          <div className="absolute inset-x-0 bottom-0 px-5 md:px-10 pb-8 md:pb-10">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[10.5px] tracking-[0.24em] uppercase text-[hsl(var(--gold-soft))]">
                  {r.annee} · {cat}
                </span>
                {r.est_phare && (
                  <span className="text-[10px] tracking-[0.22em] uppercase font-medium bg-primary/95 px-2.5 py-1 text-background">
                    Projet phare
                  </span>
                )}
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-tight text-balance max-w-4xl">
                {r.titre}
              </h1>
              {r.sous_titre && (
                <p className="font-serif italic text-lg md:text-xl text-background/80 mt-3 max-w-2xl">
                  {r.sous_titre}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── META STRIP : repères en bande horizontale moderne ── */}
      <section className="border-b border-border bg-background" data-testid="detail-meta">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 md:py-7 flex flex-wrap items-center gap-x-8 gap-y-4 text-[12.5px]">
          <div className="flex items-center gap-2.5">
            <Calendar size={14} className="text-primary" />
            <span className="text-foreground/55 text-[10.5px] tracking-[0.18em] uppercase">Année</span>
            <span className="text-foreground/90">{r.annee}</span>
          </div>
          <span className="w-px h-4 bg-border hidden md:block" />
          <div className="flex items-center gap-2.5">
            <Layers size={14} className="text-primary" />
            <span className="text-foreground/55 text-[10.5px] tracking-[0.18em] uppercase">Catégorie</span>
            <span className="text-foreground/90">{cat}</span>
          </div>
          {r.lieu && (
            <>
              <span className="w-px h-4 bg-border hidden md:block" />
              <div className="flex items-center gap-2.5">
                <MapPin size={14} className="text-primary" />
                <span className="text-foreground/55 text-[10.5px] tracking-[0.18em] uppercase">Lieu</span>
                <span className="text-foreground/90">{r.lieu}</span>
              </div>
            </>
          )}
          {r.materiaux?.length > 0 && (
            <>
              <span className="w-px h-4 bg-border hidden md:block" />
              <div className="flex items-center gap-2.5">
                <span className="text-foreground/55 text-[10.5px] tracking-[0.18em] uppercase">Matières</span>
                <span className="text-foreground/90">{r.materiaux.join(" · ")}</span>
              </div>
            </>
          )}
          {r.coordination_expert && (
            <div className="ml-auto flex items-center gap-2.5 text-primary">
              <ShieldCheck size={14} />
              <span className="text-[11px] tracking-[0.18em] uppercase font-medium">Coordination cabinet d'expertise</span>
            </div>
          )}
        </div>
      </section>

      {/* ── INTRO : chapeau en gros + paragraphes 2 col ── */}
      {(r.chapeau || paras.length > 0) && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-10 md:pb-12">
          {r.chapeau && (
            <div className={`grid md:grid-cols-12 gap-x-10 gap-y-6 ${paras.length > 0 ? "mb-10 md:mb-12" : ""}`}>
              <p className="md:col-span-2 text-eyebrow-gold pt-2">01 — Le dossier</p>
              <p className="md:col-span-10 font-serif text-2xl md:text-3xl lg:text-[34px] leading-[1.25] text-balance text-foreground/95">
                {r.chapeau}
              </p>
            </div>
          )}

          {paras.length > 0 && (
            <div className="grid md:grid-cols-12 gap-x-10 gap-y-6">
              <div className="md:col-span-2" />
              <div className="md:col-span-10 columns-1 md:columns-2 gap-10">
                {paras.map((p, i) => (
                  <p
                    key={i}
                    className="text-[15.5px] leading-[1.7] text-foreground/80 mb-5 break-inside-avoid"
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── AVANT / APRÈS ── */}
      {validPairs.length > 0 && (
        <section
          className="bg-muted/30 border-y border-border py-16 md:py-20"
          data-testid="avant-apres-section"
        >
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <div className="grid md:grid-cols-12 gap-x-10 gap-y-6 mb-10 md:mb-12 items-end">
              <div className="md:col-span-7">
                <p className="text-eyebrow-gold mb-3">02 — Avant · Après</p>
                <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">
                  L'écart entre <span className="italic text-primary">l'état initial et la livraison</span>.
                </h2>
              </div>
              <p className="md:col-span-5 text-[14.5px] leading-relaxed text-foreground/70 max-w-md md:justify-self-end">
                Glissez le curseur sur chaque image pour mesurer le travail accompli.
              </p>
            </div>
            <div className="space-y-12 md:space-y-16">
              {validPairs.map((pair, i) => (
                <BeforeAfter
                  key={`${pair.avant_url}-${pair.apres_url}`}
                  avant_url={pair.avant_url}
                  apres_url={pair.apres_url}
                  legende={pair.legende}
                  testid={`avant-apres-${i}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── GALERIE : grille uniforme propre ── */}
      {r.photos?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-x-10 gap-y-6 mb-10 md:mb-12 items-end">
            <div className="md:col-span-7">
              <p className="text-eyebrow-gold mb-3">03 — Carnet de chantier</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">
                Les <span className="italic text-primary">détails</span>.
              </h2>
            </div>
            <p className="md:col-span-5 text-[14.5px] leading-relaxed text-foreground/70 max-w-md md:justify-self-end">
              Quelques vues prises pendant l'exécution et au moment de la livraison.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-testid="detail-gallery">
            {r.photos.map((p, i) => (
              <figure key={p.url || i} className="group overflow-hidden bg-[#F5F1EA] aspect-[4/3]" data-testid={`gallery-photo-${i}`}>
                <img
                  src={p.url}
                  alt={p.legende || `Photo ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                />
                {p.legende && (
                  <figcaption className="text-[11px] tracking-[0.16em] uppercase text-foreground/55 p-3">
                    {p.legende}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* ── NAV PRÉCÉDENT / SUIVANT entre réalisations ── */}
      {(prev || next) && (
        <section className="border-y border-border bg-muted/20">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10 grid md:grid-cols-2 gap-px bg-border">
            {prev && (
              <Link
                to={`/realisations/${prev.slug}`}
                className="group bg-background p-6 md:p-8 flex items-center gap-5 hover:bg-muted/40 transition-colors"
                data-testid="detail-prev"
              >
                <ArrowLeft size={18} className="shrink-0 text-primary transition-transform group-hover:-translate-x-1" />
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-foreground/45 mb-1">Précédent</p>
                  <p className="font-serif text-lg md:text-xl leading-tight truncate group-hover:text-primary transition-colors">{prev.titre}</p>
                </div>
              </Link>
            )}
            {next && (
              <Link
                to={`/realisations/${next.slug}`}
                className="group bg-background p-6 md:p-8 flex items-center justify-end gap-5 hover:bg-muted/40 transition-colors text-right"
                data-testid="detail-next"
              >
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-foreground/45 mb-1">Suivant</p>
                  <p className="font-serif text-lg md:text-xl leading-tight truncate group-hover:text-primary transition-colors">{next.titre}</p>
                </div>
                <ArrowRight size={18} className="shrink-0 text-primary transition-transform group-hover:translate-x-1" />
              </Link>
            )}
          </div>
        </section>
      )}

      {/* ── CTA FINAL ── */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-x-10 gap-y-8 items-end">
          <div className="md:col-span-8">
            <p className="text-eyebrow text-primary mb-3">Pour un dossier similaire</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">
              {r.categorie === "sinistre" ? (
                <>Vous êtes expert, <span className="italic text-[hsl(var(--gold-soft))]">parlons-en</span>.</>
              ) : (
                <>Un projet du même esprit ? <span className="italic text-[hsl(var(--gold-soft))]">Discutons-en</span>.</>
              )}
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex flex-col gap-3">
            <Link
              to={r.categorie === "sinistre" ? "/cabinets-d-expertise" : "/contact"}
              className="inline-flex items-center justify-center gap-3 bg-primary text-background px-6 py-4 text-[12px] tracking-[0.16em] uppercase font-medium hover:bg-background hover:text-foreground transition-colors"
              data-testid="detail-cta"
            >
              {r.categorie === "sinistre" ? "Page cabinets d'expertise" : "Prendre contact"} <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/realisations"
              className="inline-flex items-center justify-center gap-3 border border-background/40 text-background px-6 py-4 text-[12px] tracking-[0.16em] uppercase font-medium hover:bg-background hover:text-foreground transition-colors"
              data-testid="detail-back-bottom"
            >
              Voir d'autres réalisations
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
